import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Card, Pill, SectionLabel } from "../components/ui";
import { Ic } from "../components/icons";
import { useNav } from "../context/NavContext";
import { PublicHeader } from "../components/PublicHeader";
import {
  plans,
  creditTopUps,
  packs,
  ENGAGEMENT_DISCOUNT_PCT,
  FREE_SIGNUP_CREDIT_DAYS,
  priceFor,
  pricePerFiche,
  formatEur,
  formatPerFiche,
  cheapestPerFichePlan,
  type BillingCycle,
  type Plan,
  type PackId,
  type PlanId,
} from "../data/pricing";
import {
  startCheckout,
  CheckoutNotConfiguredError,
  AlreadySubscribedError,
} from "../lib/checkout";
import { openCookieSettings } from "../lib/consent";
import { useEntitlements } from "../lib/useEntitlements";

type TopUpQuantity = 1 | 5 | 10 | 25;

type CheckoutIntent =
  | { kind: "subscription"; planId: PlanId; cycle: BillingCycle }
  | { kind: "topup"; planId: PlanId; quantity: TopUpQuantity }
  | { kind: "pack"; packId: PackId };

export default function Pricing() {
  const { go } = useNav();
  const { billing } = useEntitlements();
  const activePlanId = (() => {
    if (!billing || billing.plan === "free") return null;
    if (billing.status !== "active" && billing.status !== "trialing") return null;
    return billing.plan as PlanId;
  })();
  const [cycle, setCycle] = useState<BillingCycle>("engagement");
  const [checkoutError, setCheckoutError] = useState<{
    title: string;
    message: string;
  } | null>(null);
  const [busyIntent, setBusyIntent] = useState<string | null>(null);
  /** Section "Crédits add-on" repliée par défaut pour ne pas distraire les
   *  non-abonnés. Ouverte via le CTA "Besoin de plus de fiches certains mois ?"
   *  de chaque PlanCard, ou automatiquement quand on arrive avec `#credits`
   *  dans l'URL (modale d'upsell `monthly-quota-exhausted` depuis Nouvelle). */
  const [showCredits, setShowCredits] = useState<boolean>(() =>
    typeof window !== "undefined" && window.location.hash === "#credits",
  );

  const cheapest = useMemo(() => cheapestPerFichePlan(cycle), [cycle]);

  /** Ouvre la section + smooth-scroll. Appelé depuis le CTA de chaque PlanCard
   *  et depuis l'effet d'auto-ouverture (arrivée avec `#credits`). */
  const revealCredits = useCallback(() => {
    setShowCredits(true);
    // Laisse React monter la section avant de scroller.
    requestAnimationFrame(() => {
      const el = document.getElementById("credits");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash === "#credits") {
      revealCredits();
      // Consomme le hash pour éviter une ré-ouverture intempestive à un futur
      // mount (ex. retour arrière navigateur).
      history.replaceState(null, "", window.location.pathname);
    }
  }, [revealCredits]);

  async function handleCheckout(intent: CheckoutIntent, intentKey: string) {
    setCheckoutError(null);
    setBusyIntent(intentKey);
    try {
      await startCheckout(intent);
    } catch (e) {
      if (e instanceof CheckoutNotConfiguredError) {
        setCheckoutError({
          title: "Paiement bientôt disponible",
          message: e.message,
        });
      } else if (e instanceof AlreadySubscribedError) {
        // Pas de modale d'erreur : l'user a déjà une sub active. On le
        // redirige vers Settings → Abonnement où il peut voir son sub
        // actuel, le réactiver s'il est en résiliation, ou ouvrir le
        // Customer Portal pour gérer ses moyens de paiement.
        go("settings", { settingsTab: "abonnement" });
      } else {
        setCheckoutError({
          title: "Paiement impossible",
          message:
            e instanceof Error
              ? e.message
              : "Impossible de démarrer le paiement.",
        });
      }
    } finally {
      setBusyIntent(null);
    }
  }

  return (
    <div className="min-h-screen bg-canvas">
      <PublicHeader active="pricing" />

      <div className="max-w-[1240px] mx-auto px-4 sm:px-6 pt-8 sm:pt-14 pb-10 page-enter">
        {/* Accroche */}
        <div className="text-center max-w-[760px] mx-auto">
          <Pill
            tone="lime"
            icon={<Ic.Sparkle size={12} />}
            className="mx-auto"
          >
            À partir de {formatPerFiche(cheapest.perFiche)} €/fiche
          </Pill>
          <h1 className="mt-5 text-[30px] sm:text-[44px] lg:text-[54px] leading-[1.05] font-semibold tracking-tight">
            Jusqu'à <span className="font-serif italic font-normal">3× moins</span> cher{" "}
            <br className="hidden sm:block" />
            que nos concurrents.
          </h1>
          <p className="mt-4 text-[15.5px] text-ink-600 max-w-[620px] mx-auto">
            Plus de volume, plus d'analyses, et une fiche à votre marque dès le
            premier plan. Les 6 analyses (zonage, extension, surélévation,
            division, servitudes, DVF) sont incluses dans toutes les formules.
          </p>

          <BillingCycleToggle cycle={cycle} onChange={setCycle} />

          <div className="mt-3 text-[12px] text-ink-500 inline-flex items-center gap-1.5">
            <Ic.Bolt size={12} className="text-lime-500" />
            <span>
              1 fiche offerte à l'inscription — valable{" "}
              {FREE_SIGNUP_CREDIT_DAYS} jours, sans carte bleue
            </span>
          </div>
          <div className="mt-2 text-[11.5px] text-ink-500 leading-relaxed">
            Toute souscription est régie par nos{" "}
            <button
              onClick={() => {
                window.location.hash = "#cgv";
                go("legal");
              }}
              className="underline decoration-ink-300 underline-offset-2 hover:text-ink-900"
            >
              CGV
            </button>{" "}
            et notre{" "}
            <button
              onClick={() => {
                window.location.hash = "#confidentialite";
                go("legal");
              }}
              className="underline decoration-ink-300 underline-offset-2 hover:text-ink-900"
            >
              politique de confidentialité
            </button>
            .
          </div>
        </div>

        {/* Plans grid */}
        <div
          className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-4"
          role="list"
          aria-label="Plans d'abonnement"
        >
          {plans.map((p) => (
            <PlanCard
              key={p.id}
              plan={p}
              cycle={cycle}
              busy={busyIntent === `sub:${p.id}`}
              onChoose={() =>
                handleCheckout(
                  { kind: "subscription", planId: p.id, cycle },
                  `sub:${p.id}`,
                )
              }
              onWantsMoreCredits={revealCredits}
            />
          ))}
        </div>

        <p className="mt-4 text-[11.5px] text-ink-500 text-center">
          Prix HT — TVA non applicable, article 293 B du CGI (à facturer en sus
          si applicable).
        </p>

        {/* Crédits add-on (abonnés) — repliée par défaut. Attachée à la grille
            des plans (mt-6, alignée sur la même largeur et le même grid 4col)
            pour se lire comme une extension naturelle. Révélée depuis le CTA
            "Besoin de plus de fiches certains mois ?" de chaque PlanCard ou
            via l'ancre #credits (modale d'upsell monthly-quota-exhausted). */}
        {showCredits && (
          <section id="credits" className="mt-6 scroll-mt-24">
            <Card className="p-5 sm:p-7 bg-paper !shadow-none hairline">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl2 bg-ink-900 text-lime-300 grid place-items-center shrink-0">
                  <Ic.Layers size={16} />
                </div>
                <div>
                  <div className="text-[10.5px] uppercase tracking-[0.14em] font-medium text-ink-500">
                    Abonnés · Crédits add-on
                  </div>
                  <h3 className="mt-1 text-[18px] sm:text-[20px] font-semibold tracking-tight leading-tight">
                    Ajoutez des crédits à la volée ou en pré-pack.
                  </h3>
                  <p className="mt-1 text-[13px] text-ink-600 max-w-[640px] leading-relaxed">
                    Le tarif unitaire baisse avec votre plan. Les crédits
                    achetés sont valables 12 mois tant que votre abonnement
                    est actif — les pré-packs les plus gros amortissent les
                    mois de pic.
                  </p>
                </div>
              </div>

              <div className="mt-5 grid md:grid-cols-2 lg:grid-cols-4 gap-3">
                {creditTopUps.map((t) => {
                  const plan = plans.find((p) => p.id === t.planId)!;
                  const isUserPlan = activePlanId === t.planId;
                  // Bouton activé uniquement pour le plan auquel l'user est
                  // abonné. Les topups ont un prix unitaire par plan — les
                  // ouvrir cross-plan permettrait à un Starter d'acheter au
                  // tarif Chasse (8 €/fiche au lieu de 18) ce qui casse le
                  // modèle. Tooltip explicite pour les autres cards.
                  const disabled = !isUserPlan;
                  const disabledTitle = !activePlanId
                    ? "Réservé aux abonnés. Souscrivez d'abord un plan."
                    : `Réservé aux abonnés ${plan.name}.`;
                  return (
                    <div
                      key={t.planId}
                      className={`rounded-xl2 p-4 flex flex-col transition ${
                        isUserPlan
                          ? "bg-paper hairline ring-2 ring-ink-900"
                          : "bg-ink-50"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-[11.5px] font-medium text-ink-600">
                          Plan {plan.name}
                        </div>
                        {isUserPlan && (
                          <Pill tone="lime" icon={<Ic.Check size={10} />}>
                            Votre plan
                          </Pill>
                        )}
                      </div>
                      <div className="mt-1.5 flex items-baseline gap-1.5">
                        <span className="text-[28px] leading-none font-semibold tabular tracking-tight">
                          {formatEur(t.unitPrice)}&nbsp;€
                        </span>
                        <span className="text-[11px] text-ink-500">
                          / fiche
                        </span>
                      </div>
                      <div className="mt-0.5 text-[11px] text-ink-500">
                        Crédits valables 12 mois
                      </div>

                      <div className="mt-3 pt-3 border-t border-ink-200/70">
                        <div className="text-[10px] uppercase tracking-[0.12em] text-ink-500 mb-2">
                          À la volée
                        </div>
                        <TopUpBuyButton
                          quantity={1}
                          unitPrice={t.unitPrice}
                          disabled={disabled}
                          disabledTitle={disabledTitle}
                          busy={busyIntent === `topup:${t.planId}:1`}
                          onClick={() =>
                            handleCheckout(
                              { kind: "topup", planId: t.planId, quantity: 1 },
                              `topup:${t.planId}:1`,
                            )
                          }
                        />
                      </div>

                      <div className="mt-3 pt-3 border-t border-ink-200/70">
                        <div className="text-[10px] uppercase tracking-[0.12em] text-ink-500 mb-2">
                          Pré-packs
                        </div>
                        <div className="space-y-1.5">
                          {t.prepackSizes.map((n) => (
                            <TopUpBuyButton
                              key={n}
                              quantity={n}
                              unitPrice={t.unitPrice}
                              disabled={disabled}
                              disabledTitle={disabledTitle}
                              busy={busyIntent === `topup:${t.planId}:${n}`}
                              onClick={() =>
                                handleCheckout(
                                  {
                                    kind: "topup",
                                    planId: t.planId,
                                    quantity: n,
                                  },
                                  `topup:${t.planId}:${n}`,
                                )
                              }
                            />
                          ))}
                        </div>
                      </div>

                      {!isUserPlan && (
                        <div className="mt-3 text-[11px] text-ink-500 leading-snug">
                          {!activePlanId ? (
                            <>
                              Souscrivez d'abord un plan pour acheter des
                              crédits add-on.
                            </>
                          ) : (
                            <>
                              Réservé aux abonnés Plan {plan.name}.
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>
          </section>
        )}

        {/* Réseau sur devis — !bg-ink-900 force l'override du bg-paper par défaut du Card, qui sinon remporte la spécificité et rend la band quasi-invisible. */}
        <Card className="mt-10 !bg-ink-900 !shadow-none text-white border-0 p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 relative overflow-hidden">
          {/* Halo lime pour accroître le contraste et l'accroche visuelle. */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -right-20 -top-20 w-64 h-64 rounded-full bg-lime-400/15 blur-3xl"
          />
          <div className="flex items-start sm:items-center gap-5 relative">
            <div className="w-16 h-16 rounded-xl2 bg-lime-400 text-ink-900 grid place-items-center shrink-0 shadow-[0_8px_24px_rgba(132,204,22,0.35)]">
              <Ic.Building size={26} />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <Pill tone="lime">Réseau & grands comptes</Pill>
                <div className="text-[11.5px] text-lime-300 font-mono uppercase tracking-widest">
                  sur devis
                </div>
              </div>
              <div className="text-[22px] sm:text-[26px] font-semibold tracking-tight mt-2 leading-tight">
                Plus de 100 fiches / mois ou plusieurs enseignes ?
              </div>
              <div className="text-[13.5px] text-ink-200 mt-2 max-w-[560px] leading-relaxed">
                SLA contractualisé, SSO (SAML/OIDC), déploiement piloté,
                onboarding et formation des équipes.
              </div>
            </div>
          </div>
          <Button
            variant="primary"
            size="lg"
            icon={<Ic.Mail size={14} />}
            className="shrink-0 relative"
          >
            Parler au commercial
          </Button>
        </Card>

        {/* Packs ponctuels (non-abonnés) */}
        <section id="packs" className="mt-16">
          <div className="mb-6 flex items-end justify-between gap-6 flex-wrap">
            <div>
              <SectionLabel>Sans abonnement · Packs ponctuels</SectionLabel>
              <h2 className="mt-2 text-[26px] sm:text-[30px] font-semibold tracking-tight">
                Juste quelques fiches, quand vous en avez besoin.
              </h2>
              <p className="mt-2 text-[14px] text-ink-600 max-w-[560px]">
                Idéal pour tester l'outil ou couvrir un closing ponctuel. Pas
                d'abonnement, pas de reconduction.
              </p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {packs.map((pk) => (
              <Card
                key={pk.id}
                className={`p-5 flex flex-col ${
                  pk.highlight ? "ring-2 ring-ink-900 shadow-pop" : ""
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="text-[13px] font-medium text-ink-700">
                    {pk.name}
                  </div>
                  {pk.highlight && (
                    <Pill tone="lime" icon={<Ic.Star size={11} />}>
                      Recommandé
                    </Pill>
                  )}
                </div>
                <div className="mt-3 flex items-baseline gap-1.5">
                  <span className="text-[36px] leading-none font-semibold tabular tracking-tight">
                    {formatEur(pk.price)}&nbsp;€
                  </span>
                  <span className="text-[12px] text-ink-500">HT</span>
                </div>
                <div className="mt-1 text-[12px] text-ink-600 tabular">
                  {formatPerFiche(pk.price / pk.quantity)} € / fiche ·{" "}
                  {pk.quantity} fiche{pk.quantity > 1 ? "s" : ""}
                </div>
                <div className="mt-1 text-[12px] text-ink-500">
                  {pk.validityMonths > 0
                    ? `Valable ${pk.validityMonths} mois`
                    : "Usage immédiat"}
                </div>
                <Button
                  className="mt-5"
                  variant={pk.highlight ? "dark" : "soft"}
                  onClick={() =>
                    handleCheckout(
                      { kind: "pack", packId: pk.id },
                      `pack:${pk.id}`,
                    )
                  }
                  disabled={busyIntent === `pack:${pk.id}`}
                >
                  {busyIntent === `pack:${pk.id}` ? "…" : "Acheter"}
                </Button>
              </Card>
            ))}
          </div>
        </section>

        {/* Comparison table */}
        <section className="mt-16">
          <div className="mb-6">
            <SectionLabel>Comparatif détaillé</SectionLabel>
            <h2 className="mt-2 text-[26px] sm:text-[30px] font-semibold tracking-tight">
              Tout ce qui change, plan par plan.
            </h2>
          </div>
          <Card className="p-0 overflow-hidden">
            <div className="overflow-x-auto">
              <ComparisonTable />
            </div>
          </Card>
        </section>

        {/* FAQ */}
        <section className="mt-16">
          <div className="mb-6 text-center">
            <SectionLabel className="!text-center">
              Questions fréquentes
            </SectionLabel>
            <h2 className="mt-2 text-[26px] sm:text-[30px] font-semibold tracking-tight">
              Tout ce que nos clients demandent avant de signer.
            </h2>
          </div>
          <div className="max-w-[780px] mx-auto">
            <Faq />
          </div>
        </section>

        {/* CTA bottom */}
        <section className="mt-16 mb-16">
          <Card className="p-5 sm:p-10 bg-lime-300 !shadow-none text-center">
            <div className="text-[10.5px] uppercase tracking-widest font-mono text-ink-700">
              Démarrez en 30 secondes
            </div>
            <h2 className="mt-3 text-[24px] sm:text-[36px] font-semibold tracking-tight">
              Essayez Arpentia sur votre prochain RDV.
            </h2>
            <p className="mt-3 text-[14px] text-ink-800 max-w-[520px] mx-auto">
              1 fiche offerte à la création de compte, valable{" "}
              {FREE_SIGNUP_CREDIT_DAYS} jours. Sans carte bleue. Sans
              reconduction automatique.
            </p>
            <div className="mt-6 flex items-center gap-2 justify-center flex-wrap">
              <Button
                variant="dark"
                size="lg"
                onClick={() => go("nouvelle")}
                iconRight={<Ic.Arrow size={14} />}
              >
                Créer ma première fiche
              </Button>
              <Button
                variant="soft"
                size="lg"
                onClick={() => go("landing")}
              >
                Retour à l'accueil
              </Button>
            </div>
          </Card>
        </section>
      </div>

      {checkoutError && (
        <CheckoutNoticeModal
          title={checkoutError.title}
          message={checkoutError.message}
          onClose={() => setCheckoutError(null)}
        />
      )}

      <footer className="border-t border-ink-200/60 mt-16">
        <div className="max-w-[1240px] mx-auto px-4 sm:px-6 py-6 flex items-center justify-between flex-wrap gap-4 text-[12px] text-ink-500">
          <span>© 2026 Arpentia SAS — 8 rue d'Uzès, 75002 Paris.</span>
          <div className="flex items-center gap-4 sm:gap-5 flex-wrap">
            <button
              onClick={() => {
                window.location.hash = "#mentions-legales";
                go("legal");
              }}
              className="hover:text-ink-900 transition"
            >
              Mentions légales
            </button>
            <button
              onClick={() => {
                window.location.hash = "#cgv";
                go("legal");
              }}
              className="hover:text-ink-900 transition"
            >
              CGV
            </button>
            <button
              onClick={() => {
                window.location.hash = "#confidentialite";
                go("legal");
              }}
              className="hover:text-ink-900 transition"
            >
              Confidentialité
            </button>
            <button
              onClick={openCookieSettings}
              className="hover:text-ink-900 transition"
            >
              Gérer mes cookies
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ─── Toggle de cycle de facturation ──────────────────────────────────────

function BillingCycleToggle({
  cycle,
  onChange,
}: {
  cycle: BillingCycle;
  onChange: (c: BillingCycle) => void;
}) {
  return (
    <div
      role="radiogroup"
      aria-label="Cycle de facturation"
      className="mt-7 inline-flex items-center gap-1 bg-paper hairline rounded-full p-1"
    >
      <button
        type="button"
        role="radio"
        aria-checked={cycle === "engagement"}
        onClick={() => onChange("engagement")}
        className={`px-5 h-10 rounded-full text-[13px] inline-flex items-center gap-2 transition ${
          cycle === "engagement"
            ? "bg-ink-900 text-white"
            : "text-ink-600 hover:text-ink-900"
        }`}
      >
        Avec engagement
        <span
          className={`px-1.5 py-0.5 rounded-full text-[10.5px] font-semibold ${
            cycle === "engagement"
              ? "bg-lime-400 text-ink-900"
              : "bg-lime-200 text-ink-900"
          }`}
        >
          −{ENGAGEMENT_DISCOUNT_PCT}&nbsp;%
        </span>
      </button>
      <button
        type="button"
        role="radio"
        aria-checked={cycle === "monthly"}
        onClick={() => onChange("monthly")}
        className={`px-5 h-10 rounded-full text-[13px] transition ${
          cycle === "monthly"
            ? "bg-ink-900 text-white"
            : "text-ink-600 hover:text-ink-900"
        }`}
      >
        Mensuel
      </button>
    </div>
  );
}

// ─── Card d'un plan d'abonnement ─────────────────────────────────────────

function PlanCard({
  plan,
  cycle,
  busy,
  onChoose,
  onWantsMoreCredits,
}: {
  plan: Plan;
  cycle: BillingCycle;
  busy: boolean;
  onChoose: () => void;
  /** Ouvre la section "Crédits add-on" (cachée par défaut) et y scrolle. */
  onWantsMoreCredits: () => void;
}) {
  const price = priceFor(plan, cycle);
  const perFiche = pricePerFiche(plan, cycle);
  const highlight = !!plan.highlight;

  return (
    <Card
      className={`p-5 sm:p-6 flex flex-col relative ${
        highlight ? "ring-2 ring-ink-900 shadow-pop" : ""
      }`}
      role="listitem"
    >
      {highlight && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <Pill tone="lime" icon={<Ic.Star size={11} />}>
            Populaire
          </Pill>
        </div>
      )}
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[14px] font-semibold text-ink-900">
            {plan.name}
          </div>
          <div className="text-[11.5px] text-ink-500 mt-0.5">
            {plan.audience}
          </div>
        </div>
      </div>

      <div
        className="mt-4 flex items-baseline gap-1.5"
        aria-live="polite"
        aria-atomic="true"
      >
        <span className="text-[44px] leading-none font-semibold tracking-tight tabular">
          {formatEur(price)}&nbsp;€
        </span>
        <span className="text-[12px] text-ink-500">HT / mois</span>
      </div>
      {/* En mode engagement, l'abonnement est annuel prépayé : on affiche le total facturé d'un coup pour éviter toute confusion au checkout. */}
      <div className="text-[11.5px] text-ink-500 mt-1 leading-snug">
        {cycle === "engagement" ? (
          <>
            Soit {formatEur(price * 12)}&nbsp;€ HT facturés à la souscription ·
            renouvellement annuel automatique
          </>
        ) : (
          <>Sans engagement · résiliation à tout moment</>
        )}
      </div>

      <div className="mt-3 inline-flex items-center gap-2 text-[12px] text-ink-700 bg-ink-50 hairline rounded-full px-2.5 py-1 w-fit tabular">
        <Ic.Layers size={11} />
        {formatPerFiche(perFiche)} €/fiche
      </div>

      <p className="mt-3 text-[12.5px] text-ink-700 leading-snug">
        {plan.tagline}
      </p>

      <ul className="mt-4 space-y-2 text-[13px] flex-1">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2">
            <Ic.Check size={14} className="text-forest mt-0.5 shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <Button
        className="mt-5"
        variant={highlight ? "dark" : "soft"}
        onClick={onChoose}
        disabled={busy}
      >
        {busy ? "…" : `Choisir ${plan.name}`}
      </Button>

      <button
        type="button"
        onClick={onWantsMoreCredits}
        className="mt-3 text-[11.5px] text-ink-500 hover:text-ink-900 text-center underline underline-offset-2 decoration-ink-300 hover:decoration-ink-900"
      >
        Besoin de plus de fiches certains mois ?
      </button>
    </Card>
  );
}

// ─── Bouton d'achat d'un pré-pack topup ──────────────────────────────────

function TopUpBuyButton({
  quantity,
  unitPrice,
  disabled,
  disabledTitle,
  busy,
  onClick,
}: {
  quantity: number;
  unitPrice: number;
  disabled: boolean;
  disabledTitle: string;
  busy: boolean;
  onClick: () => void;
}) {
  const total = quantity * unitPrice;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || busy}
      title={disabled ? disabledTitle : `Acheter ${quantity} fiche${quantity > 1 ? "s" : ""}`}
      className={`w-full flex items-baseline justify-between text-[12.5px] px-2.5 h-9 rounded-md transition ${
        disabled
          ? "text-ink-400 cursor-not-allowed bg-transparent"
          : "text-ink-700 hover:bg-ink-900 hover:text-white bg-paper hairline"
      }`}
    >
      <span>
        {busy ? "…" : `${quantity} fiche${quantity > 1 ? "s" : ""}`}
      </span>
      <span className="font-semibold tabular">
        {formatEur(total)}&nbsp;€
      </span>
    </button>
  );
}

// ─── Modale "paiement bientôt disponible" (scaffold Stripe) ──────────────

function CheckoutNoticeModal({
  title,
  message,
  onClose,
}: {
  title: string;
  message: string;
  onClose: () => void;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="checkout-notice-title"
      className="fixed inset-0 z-50 grid place-items-center bg-ink-900/40 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <Card
        className="max-w-[420px] w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <Pill tone="lime" icon={<Ic.Bolt size={12} />}>
            Paiement
          </Pill>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-ink-100 grid place-items-center text-ink-500"
            aria-label="Fermer"
          >
            <Ic.Cross size={14} />
          </button>
        </div>
        <h3
          id="checkout-notice-title"
          className="mt-3 text-[18px] font-semibold tracking-tight"
        >
          {title}
        </h3>
        <p className="mt-2 text-[13.5px] text-ink-700 leading-relaxed">
          {message}
        </p>
        <Button className="mt-5 w-full" variant="dark" onClick={onClose}>
          Compris
        </Button>
      </Card>
    </div>
  );
}

// ─── Table comparative ───────────────────────────────────────────────────

type RowSection = { sec: string };
type RowFeature = { l: string; v: (string | boolean)[] };
type Row = RowSection | RowFeature;

const isSection = (r: Row): r is RowSection => "sec" in r;

const ComparisonTable = () => {
  const planNames = plans.map((p) => p.name);
  const highlightIdx = plans.findIndex((p) => p.highlight);
  const rows: Row[] = [
    { sec: "Usage" },
    {
      l: "Fiches incluses / mois",
      v: plans.map((p) => String(p.ficheQuota)),
    },
    {
      l: "Export PDF à votre marque",
      v: [true, true, true, true],
    },
    { sec: "Analyses" },
    { l: "Zonage PLU & règlement", v: [true, true, true, true] },
    { l: "Extension + surélévation", v: [true, true, true, true] },
    { l: "Division parcellaire", v: [true, true, true, true] },
    { l: "Servitudes & risques", v: [true, true, true, true] },
    { l: "Comparables DVF", v: [true, true, true, true] },
    { l: "Historique DVF enrichi", v: [false, false, false, true] },
    { sec: "Équipe & intégrations" },
    {
      l: "Utilisateurs inclus",
      v: ["1", "1", "5", "10"],
    },
    { l: "Modèles PDF personnalisés", v: [false, true, true, true] },
    { l: "Branding multi-vitrines", v: [false, false, true, true] },
    { l: "API & export batch CSV", v: [false, false, false, true] },
    { sec: "Support" },
    { l: "Support email", v: [true, true, true, true] },
    { l: "Support prioritaire", v: [false, true, true, true] },
    { l: "Account manager dédié", v: [false, false, false, true] },
  ];
  return (
    <table className="w-full text-[13px]">
      <thead>
        <tr className="bg-ink-50 text-left">
          <th className="px-5 py-3 font-medium text-ink-600 w-[36%]"></th>
          {planNames.map((p, i) => (
            <th
              key={p}
              className={`px-4 py-3 font-semibold text-ink-900 text-center ${
                i === highlightIdx ? "bg-lime-100" : ""
              }`}
            >
              {p}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) =>
          isSection(r) ? (
            <tr key={"s" + i} className="border-t border-ink-200/70 bg-canvas">
              <td
                colSpan={planNames.length + 1}
                className="px-5 py-2.5 text-[10.5px] uppercase tracking-[0.14em] font-medium text-ink-500"
              >
                {r.sec}
              </td>
            </tr>
          ) : (
            <tr key={i} className="border-t border-ink-200/70">
              <td className="px-5 py-3 text-ink-700">{r.l}</td>
              {r.v.map((val, j) => (
                <td
                  key={j}
                  className={`px-4 py-3 text-center ${
                    j === highlightIdx ? "bg-lime-100/60" : ""
                  }`}
                >
                  {typeof val === "boolean" ? (
                    val ? (
                      <Ic.Check size={15} className="text-forest inline" />
                    ) : (
                      <span className="text-ink-300">—</span>
                    )
                  ) : (
                    <span className="tabular font-medium">{val}</span>
                  )}
                </td>
              ))}
            </tr>
          ),
        )}
      </tbody>
    </table>
  );
};

// ─── FAQ ─────────────────────────────────────────────────────────────────

const Faq = () => {
  const [open, setOpen] = useState<number>(0);
  const qs = [
    {
      q: "Quelle est la différence entre « Avec engagement » et « Mensuel » ?",
      a: "« Avec engagement » = abonnement annuel, facturé en une fois à la souscription, avec 20 % de réduction sur le tarif mensuel. Il se renouvelle automatiquement chaque année (préavis de résiliation de 30 jours avant la date anniversaire). « Mensuel » = sans engagement, prélevé chaque mois, résiliable à tout moment au tarif plein.",
    },
    {
      q: "Que se passe-t-il si j'épuise mon quota de fiches dans le mois ?",
      a: "Vous pouvez acheter des crédits add-on à la volée (auto-facturés au tarif unitaire de votre plan) ou en pré-pack (5/10/25 fiches). Les crédits achetés restent valables 12 mois tant que votre abonnement est actif.",
    },
    {
      q: "Puis-je changer de plan en cours d'abonnement ?",
      a: "Oui, à tout moment. Une montée de plan est appliquée immédiatement avec prorata. Une descente de plan prend effet au prochain cycle de facturation. Vos fiches déjà générées restent accessibles.",
    },
    {
      q: "Les 6 analyses sont-elles toutes incluses dans le plan Starter ?",
      a: "Oui, absolument. Zonage PLU, extension, surélévation, division parcellaire, servitudes & risques, comparables DVF — toutes les formules (Starter à Chasse Foncière) donnent accès aux 6 analyses. La seule différence est le volume de fiches inclus et les outils d'équipe.",
    },
    {
      q: "Comment fonctionne la fiche gratuite à l'inscription ?",
      a: `1 fiche offerte à la création de votre compte, valable ${FREE_SIGNUP_CREDIT_DAYS} jours. Aucune carte bleue demandée. Au-delà, choisissez un abonnement ou un pack ponctuel — les fiches déjà générées restent accessibles.`,
    },
    {
      q: "La couverture est-elle vraiment nationale ?",
      a: "Oui — Arpentia couvre l'intégralité des communes dont le PLU est disponible sur le Géoportail de l'urbanisme, soit plus de 34 000 communes. Pour les rares communes en carte communale ou RNU, une fiche simplifiée est générée.",
    },
  ];
  return (
    <div className="space-y-3">
      {qs.map((it, i) => (
        <Card key={i} className="p-0 overflow-hidden">
          <button
            onClick={() => setOpen(open === i ? -1 : i)}
            className="w-full px-5 py-4 flex items-center justify-between gap-4 text-left"
            aria-expanded={open === i}
          >
            <span className="text-[14.5px] font-medium">{it.q}</span>
            <span
              className={`w-8 h-8 rounded-full bg-ink-50 grid place-items-center transition ${
                open === i ? "rotate-180" : ""
              }`}
              aria-hidden="true"
            >
              <Ic.ChevronDown size={14} />
            </span>
          </button>
          {open === i && (
            <div className="px-5 pb-5 -mt-1 text-[13.5px] text-ink-700 leading-relaxed">
              {it.a}
            </div>
          )}
        </Card>
      ))}
    </div>
  );
};
